import { BlogCard } from './types';

export const HeatRashInChildrenBlogCards: BlogCard[] = [
  {
    title: {
      content: '### Heat Rash in Children: A Paediatrician’s Guide for Summer',
      styles: {
        h3: 'font-Mulish font-bold text-royal-875 my-3 text-base md:text-lg leading-tight line-clamp-2',
      },

      topicsStyles: {
        h3: 'text-theme-primary text-3xl md:text-4xl my-4 md:my-6 p-2 font-black ',
      },
    },
    description: {
      content:
        'This guide explains what heat rash in children looks like, what causes it, the best ways to treat it at home, and how to tell it apart from sun allergy, another common summer skin condition that parents often confuse it with.',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    imageUrl: 'hash-rash-hero-blog.webp',
    bannerUrl: 'hash-rash-hero-blog.webp',
    slug: 'heat-rash-in-children',
    contentType: 'article',
    doctorSlug: 'emanuela-manea',
    bannerDescription: `Heat rash in children is a common issue parents bring to a paediatric consultation during the summer months. Also known as prickly heat or miliaria, it is usually harmless and settles quickly once the skin is cooled. But knowing what you are looking at, and when to seek advice, can make a significant difference to how you manage it at home.\n\nThis guide explains what heat rash in children looks like, what causes it, the best ways to treat it at home, and how to tell it apart from sun allergy, another common summer skin condition that parents often confuse it with.`,
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'What Does Heat Rash Look Like in Children?',
          id: 'what-does-heat-rash-look-like-in-children',
        },
        {
          text: 'What Causes Heat Rash in Children?',
          id: 'what-causes-heat-rash-in-children',
        },
        {
          text: 'How to Treat Heat Rash in Children at Home',
          id: 'how-to-treat-heat-rash-in-children-at-home',
        },
        {
          text: 'Sun Allergy in Children: What Is Polymorphic Light Eruption?',
          id: 'sun-allergy-in-children-what-is-polymorphic-light-eruption',
        },
        {
          text: 'How Is Sun Allergy Different from Heat Rash?',
          id: 'how-is-sun-allergy-different-from-heat-rash',
        },
        {
          text: 'When Should You See a Paediatrician About Heat Rash or Sun Allergy?',
          id: 'when-should-you-see-a-paediatrician-about-heat-rash-or-sun-allergy',
        },
        {
          text: 'Heat Rash in Children: Frequently Asked Questions',
          id: 'heat-rash-in-children-frequently-asked-questions',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    // topics: {
    //   title: {
    //     content: '#### TOPICS:',
    //     styles: {
    //       h4: 'text-theme-primary font-extrabold p-2',
    //     },
    //   },
    //   topics: ['Colic in Babies', 'Paediatrics'],
    //   styles: {
    //     p: 'p-2 bg-info-500-opac-10 text-teal-450 bg-info-500-soft',
    //   },
    // },
    speciality: 'Paediatrics',
    iconUrl: 'emanuela-manea.webp',
    date: {
      content: '27, July 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    postimonial: {
      content: 'BSc, MBChB, FRCS (Tr&Orth) \n Consultant Paediatrician',
      styles: {
        p: 'text-xl whitespace-pre-line',
      },
    },
    author: {
      content: 'Dr Emanuela Manea',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    content: {
      content: `
<span id="what-does-heat-rash-look-like-in-children"></span>
### What Does Heat Rash Look Like in Children?

Heat rash in children, or miliaria, develops when sweat becomes trapped in blocked sweat ducts. Rather than evaporating, sweat leaks into the surrounding skin, causing irritation and a rash. Babies and young children are particularly prone to heat rash because their sweat ducts are not yet fully developed.

There are four types of heat rash in children, each presenting slightly differently.

<div style="display:flex;flex-wrap:wrap;gap:24px;margin:2rem 0;">

  <div style="flex:1 1 250px;max-width:300px;border:1px solid #E5E7EB;border-radius:16px;overflow:hidden;background:#fff;">

<img src="hash-rash-cards-first-image.webp" alt="Miliaria Crystallina" title="300x260" style="height:260px;width:100%;display:block;" />

<div style="padding:20px;">
<h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;">Miliaria Crystallina</h4>
<p style="margin:0; font-size: 12px;">This is the mildest form of heat rash. It appears as tiny clear superficial blisters of one to two millimetres that resemble beads of sweat, with no surrounding redness. The blisters break easily and may leave fine scaling behind. It most commonly affects the head, neck and upper chest.</p>
</div>
  </div>

  <div style="flex:1 1 250px;max-width:300px;border:1px solid #E5E7EB;border-radius:16px;overflow:hidden;background:#fff;">

<img src="hash-rash-cards-second-image.webp" alt="Miliaria Rubra" title="300x260" style="height:260px;width:100%;display:block;" />

<div style="padding:20px;">
<h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;">Miliaria Rubra <span style="font-size:14px">(Prickly Heat)</span></h4>
<p style="margin:0;font-size:12px">Miliaria rubra is the most common type of heat rash and the one most parents are referring to when they say prickly heat. It causes small red bumps and tiny blisters with an intense itching or prickling sensation and surrounding redness. In children it most often affects the trunk, neck folds, armpits and groin.</p>
</div>
  </div>

  <div style="flex:1 1 250px;max-width:300px;border:1px solid #E5E7EB;border-radius:16px;overflow:hidden;background:#fff;">

<img src="hash-rash-cards-third-image.webp" alt="Miliaria Pustulosa" title="300x260" style="height:260px;width:100%;display:block;" />

<div style="padding:20px;">
<h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;">Miliaria Pustulosa</h4>
<p style="margin:0;font-size:12px">This is a variant of miliaria rubra that develops when the rash becomes more inflamed. It produces small pus-filled spots and can be mistaken for a skin infection. It does not always mean the skin is infected but does warrant a paediatric review if you are unsure.</p>
</div>
  </div>

  <div style="flex:1 1 250px;max-width:300px;border:1px solid #E5E7EB;border-radius:16px;overflow:hidden;background:#fff;">

<img src="hash-rash-cards-fourth-image.webp" alt="Miliaria Profunda" title="300x260" style="height:260px;width:100%;display:block;" />

<div style="padding:20px;">
<h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;">Miliaria Profunda</h4>
<p style="margin:0;font-size:12px">Less common and deeper than the other types, miliaria profunda produces flesh-coloured firm bumps of one to three millimetres that are usually not itchy. It tends to develop after repeated episodes of miliaria rubra and most commonly affects the trunk, arms and legs.</p>
</div>
  </div>

</div>

<span id="what-causes-heat-rash-in-children"></span>

### What Causes Heat Rash in Children?

<div align="center">\n\n
![Free skincare day at Battersea Power Station](hash-rash-first-image.webp "1024x400")\n\n
</div>

Heat rash develops when sweat ducts become blocked, preventing sweat from reaching the surface of the skin. The trapped sweat leaks into surrounding tissue, causing irritation and inflammation.

Common causes and triggers of heat rash in children during UK summers include:

- Hot and humid weather, particularly during heatwaves
- Overdressing or wrapping babies too warmly
- Tight-fitting clothing that traps heat against the skin
- Excessive physical activity or sweating
- Fever
- Heavy or occlusive creams and ointments applied to the skin
- Prolonged time in a car seat or buggy where air cannot circulate freely
- Some medications

A baby overheating rash is one of the most common presentations in clinic during warm weather. Parents often do not realise how quickly babies can overheat, particularly when dressed in multiple layers or kept in a car seat whilst travelling on a warm day. The heat rash itself is the skin’s visible response to that excess heat and trapped sweat.

Heat rash is not contagious and cannot be spread from one child to another.

<span id="how-to-treat-heat-rash-in-children-at-home"></span>

### How to Treat Heat Rash in Children at Home

The most effective treatment for prickly heat in children is straightforward: cool the skin, reduce sweating and let the skin breathe. Most cases of heat rash in children resolve within a few days with simple home measures.

#### Cool the Skin and Environment

- Move to a cooler room or environment as soon as possible
- Use a fan or air conditioning if available to cool the room/bedroom to a comfortable temperature
- Apply a cool damp cloth or compress to the affected area for 10 to 15 minutes at a time
- Give a cool bath or shower

#### Adjust Clothing

- Dress your child in loose, lightweight clothing
- Choose breathable fabrics such as cotton
- Remove wet or sweaty clothing and diapers promptly
- For heat rash in babies, check that your baby is not dressed in more layers than you would be comfortable wearing yourself

#### Skin Care

- Keep the skin clean and dry
- Avoid heavy ointments and greasy creams, which can further block sweat ducts
- Light, non-occlusive moisturisers can be used if the skin is dry, but are generally unnecessary
- Avoid soap directly on the rash; use a soap-free wash if needed

#### Medications for Heat Rash in Children

For a heat rash toddler or older child who is particularly uncomfortable:
- A mild hydrocortisone cream can help reduce inflammation and itching
- Oral antihistamines may help with itch, particularly if it is disturbing sleep
- Treatment of fever with paracetamol

<span style="font-weight: bold">What to avoid:</span> Talcum powder is sometimes suggested for heat rash but is not recommended for babies and young children due to the risk of inhalation. Avoid it entirely in children under two.

<span id="sun-allergy-in-children-what-is-polymorphic-light-eruption"></span>

### What Does Sun Allergy Look Like in Children?

Symptoms of polymorphic light eruption typically appear within a few hours to two days after sun exposure and may persist for several days to weeks. Common features include:

- Itchy red bumps (papules)
- Red patches or plaques
- Small blisters
- Occasionally hive-like raised wheals

The rash most commonly appears on sun-exposed areas: the arms, lower legs, upper chest and the V of the neck. Interestingly, the face and backs of the hands are less commonly affected, likely because these areas receive regular sun exposure and develop a degree of natural tolerance over time.

<span id="how-is-sun-allergy-different-from-heat-rash"></span>

### How Is Sun Allergy Different from Heat Rash?

| | Heat Rash | Sun Allergy (PMLE) |\n|---|---|---|\n| Average wait to be seen | Blocked sweat ducts | Immune reaction to UV light |\n| Trigger | Heat and sweating | Sun exposure |\n| Appears | Where skin is covered or in folds | On sun exposed areas |\n| Timing | During or after overheating | Hours to 1-2 days after direct sun exposure |\n| Improves with | Cooling the skin | Avoiding further sun exposure|

### How to Manage Sun Allergy in Children?

<div align="center">\n\n
![Free skincare day at Battersea Power Station](hash-rash-second-image.webp "1024x400")\n\n
</div>

The main aim of treatment for polymorphic light eruption in children is to reduce symptoms and prevent further sun-triggered reactions.

- Use a broad-spectrum SPF 50+ sunscreen that protects against both UVA and UVB radiation, applied generously and reapplied regularly
- Avoid direct sunlight during peak UV hours, typically 11am to 3pm in the UK
- Use sun-protective clothing including long sleeves, wide-brimmed hats and UV-protective swimwear
- Apply cool compresses to itchy or inflamed areas
- A topical corticosteroid cream may be recommended by a healthcare professional if the rash is particularly inflamed or itchy

Many children find that sun allergy symptoms improve as the summer progresses, as repeated low-level sun exposure gradually builds a degree of skin tolerance. However, PMLE often recurs the following year after the first significant sun exposure in spring.

Because UVA penetrates window glass, symptoms can occasionally develop even indoors near a sunny window or during car journeys. If your child appears to react to sun exposure through glass, this is worth raising at a paediatric review.

<span id="when-should-you-see-a-paediatrician-about-heat-rash-or-sun-allergy"></span>

### When Should You See a Paediatrician About Heat Rash or Sun Allergy?

<div align="center">\n\n
![Free skincare day at Battersea Power Station](hash-rash-third-image.webp "1024x400")\n\n
</div>

Most cases of heat rash in children and mild sun allergy resolve without medical treatment. However, seek a paediatric review if your child:

- Has a rash that does not improve within a few days despite cooling measures
- Develops pus, crusting or increasing pain suggesting a secondary skin infection
- Has a fever and appears unwell alongside the rash
- Has a rash that is spreading or changing rapidly
- Has had a suspected sun allergy reaction and you are unsure whether it is PMLE or something else
- Has underlying eczema or a skin condition that may be complicating the picture
- Is a young baby with a persistent rash and you are concerned

If you would like a clear clinical assessment of your child’s skin, a paediatric skin consultation can help identify the cause, rule out other conditions and give you specific guidance for your child’s skin. Appointments are available across London clinics and online.

<span id="heat-rash-in-children-frequently-asked-questions"></span>
`,
      styles: {
        h3: 'text-lg md:text-[32px] font-bold text-theme-primary mb-4',
        h4: 'text-lg md:text-[20px] font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action',
        em: 'not-italic font-bold',
        img: 'block w-full h-auto rounded-2xl overflow-hidden',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        table:
          'w-full table-fixed border-collapse text-xs md:text-sm border-2 border-black',
        th: 'border-2 border-black bg-text-muted-blue px-2 py-2 md:px-4 md:py-3 text-left font-semibold text-theme-primary whitespace-normal wrap-break-word',
        td: 'border-2 border-black px-2 py-2 md:px-4 md:py-3 align-top font-semibold whitespace-normal wrap-break-word',
      },
    },
    faq: {
      heading: {
        content: '#### Frequently asked questions (FAQS)',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'How long does heat rash last in children?',
          },
          answer: {
            content:
              'Most cases of heat rash in children resolve within a few days once the skin is kept cool and sweating is reduced. Miliaria crystallina often clears within 24 hours. Miliaria rubra (prickly heat) typically settles within three to four days. If the rash persists beyond a week despite cooling measures, a paediatric review is recommended.',
          },
        },
        {
          question: { content: 'Is heat rash the same as prickly heat?' },
          answer: {
            content:
              'Yes. Prickly heat is the common name for miliaria rubra, the most common type of heat rash. The prickling or stinging sensation it causes gives it its name. The terms heat rash, prickly heat and sweat rash are all used by parents to describe the same condition.',
          },
        },
        {
          question: {
            content:
              'How do I tell the difference between heat rash and eczema?',
          },
          answer: {
            content:
              'Heat rash vs eczema is a question parents frequently ask. Heat rash tends to appear suddenly during hot weather in previously normal skin, resolves quickly with cooling and does not leave lasting skin changes. Eczema is a chronic condition characterised by dry, itchy, inflamed skin that recurs in the same areas and is not directly triggered by heat alone, though warm weather can worsen it in some patients. If you are unsure which you are dealing with, a paediatric assessment can help clarify the diagnosis.',
          },
        },
        {
          question: {
            content: 'Why does my baby keep getting heat rash on their neck?',
          },
          answer: {
            content: `Heat rash on the neck is particularly common in babies because the neck folds trap heat, moisture and sweat. Keeping the neck area cool, dry and well ventilated, dressing your baby in loose-fitting clothing and avoiding overdressing are the most effective ways to prevent recurrent heat rash in this area.`,
          },
        },
        {
          question: {
            content: 'Can sun allergy in children be confused with heat rash?',
          },
          answer: {
            content:
              'Yes, this is one of the most common sources of confusion in summer. Sun allergy (polymorphic light eruption) produces a rash on sun-exposed areas after UV exposure, while heat rash tends to appear where the skin is covered or in body folds after sweating. The comparison table above outlines the key differences. If you are unsure which your child has, a paediatric review can help.',
          },
        },
        {
          question: {
            content:
              'When should I be concerned about a rash in my child during summer?',
          },
          answer: {
            content:
              'A rash that is non-blanching (does not fade when pressed), accompanied by a high fever, rapidly spreading, associated with difficulty breathing, or in a baby who appears unwell all warrant prompt medical attention. Most summer rashes are harmless, but when in doubt, getting a clinical assessment is always the right decision.',
          },
        },
      ],
    },
    postFaqContent: {
      content: `
      <div class="bg-surface-secondary border border-border-default rounded-lg p-4 my-6">
        If you are worried about your child’s skin this summer, whether it is a persistent heat rash, a suspected sun allergy, or a rash you cannot identify, I am here to help. Appointments are available at clinics across London including Canary Wharf, Battersea and Holborn, as well as online. Visit the How Appointments Work page or request a consultation directly.
      </div>\n\n
><span style="font-weight: bold; color: #2A347A">ⓘ Disclaimer:</span>
>
>The content on this blog is written by Dr Emanuela Manea, Consultant Paediatrician, and is intended to provide general health information for parents and carers. It does not constitute medical advice and should not be used to diagnose, treat or manage any medical condition. Every child is different, and clinical findings and treatment decisions should always be made in the context of an individual assessment by a qualified healthcare professional. If you have concerns about your child’s health, please seek appropriate medical advice promptly. If your child is seriously unwell, call 999 or go to your nearest emergency department.
`,
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        h3: 'text-lg md:text-xl font-bold text-theme-primary mt-8 mb-4',
        a: 'text-text-action font-bold',
        em: 'italic',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 rounded-lg font-bold no-underline cursor-pointer mt-8 py-3 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        blockquote:
          'border-l-4 border-theme-primary bg-surface-secondary border border-blue-200 rounded-lg p-4 my-8 text-sm text-text-default not-italic',
      },
    },
  },
];
